import { DestroyRef, effect, inject, Injectable } from '@angular/core';
import type { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { HlmDialogOpener } from '@lite-workspace/shared/helm-ui/dialog';
import { IS_MOBILE } from '@lite-workspace/utils';
import type { LibraryRequest } from '../catalog.types';
import { OnboardingService } from '../onboarding.service';
import { LessonLibraryComponent, type LessonLibraryContext } from './lesson-library.component';
import { WelcomeVideoDialogComponent, type WelcomeVideoResult } from './welcome-video-dialog.component';

/**
 * Единственная дверь в библиотеку уроков: знак вопроса в шапке и обложка на карточке коуча зовут
 * `openLibrary()`, а окно поднимает этот сервис. Никакого vcr у него нет — библиотека не принадлежит
 * экрану и переживает его смену, поэтому диалог живёт на корневом инжекторе. Приветствие новичка —
 * дверь перед дверью: его окно поднимает тот же сервис.
 */
@Injectable({ providedIn: 'root' })
export class LessonLibraryLauncherService {
  private readonly onboarding = inject(OnboardingService);
  private readonly dialogs = inject(HlmDialogOpener);
  private readonly isMobile = inject(IS_MOBILE);

  private open: BrnDialogRef<unknown> | null = null;
  private welcomeOpen = false;

  constructor() {
    effect(() => {
      if (!this.onboarding.welcome() || this.welcomeOpen) return;

      this.welcomeOpen = true;
      // Фокус на само окно: первая кнопка — обложка, и её рамка фокуса горела бы при каждом показе.
      const ref = this.dialogs.open<object, WelcomeVideoResult | undefined>(WelcomeVideoDialogComponent, undefined, undefined, {
        autoFocus: 'dialog',
      });
      // Клик мимо и Esc закрывают окно без итога — это то же «Позже».
      ref.closed$.subscribe(result => {
        this.welcomeOpen = false;
        this.onboarding.closeWelcome(result === 'watch');
      });
    });

    effect(() => {
      const request = this.onboarding.libraryRequest();
      // Повторный запрос при открытом окне игнорируется: второй диалог поверх первого — это два
      // плеера сразу, а закрытие верхнего оставило бы нижний висеть без запроса.
      if (!request || this.open) return;

      this.show(request);
    });

    inject(DestroyRef).onDestroy(() => this.open?.close(undefined));
  }

  private show({ lessonId, autoplay }: LibraryRequest): void {
    const context = { lessonId, autoplay };
    // На телефоне фокус на само окно: первым в DOM стоит поиск, и его фокус поднимал клавиатуру
    // и уводил шапку листа из вида. На большом экране поиск ловит фокус сразу.
    const ref = this.isMobile()
      ? this.dialogs.open<LessonLibraryContext>(LessonLibraryComponent, undefined, context, { autoFocus: 'dialog' })
      : this.dialogs.open<LessonLibraryContext>(LessonLibraryComponent, undefined, context);
    this.open = ref;
    // Закрыть окно может и крестик, и Esc, и клик мимо — запрос гасится там, где диалог реально умер,
    // иначе следующий `openLibrary()` пришёл бы на непогашенный прошлый.
    ref.closed$.subscribe(() => {
      this.open = null;
      this.onboarding.closeLibrary();
    });
  }
}
